import { Clock } from "three";
import { arraysEqual } from "../../lib/misc/util";
import { BufferManager } from "../managers/buffer";

export type TimeOfDay = "DAY" | "SUNSET" | "NIGHT";

export const SECS_IN_HOUR = 60 * 60;
export const MS_IN_HOUR = SECS_IN_HOUR * 1000;

export const SECS_IN_DAY = SECS_IN_HOUR * 24;

/**
 * Class for managing clock state
 * Updates the master `now` state (which is the relative "game" time from the
 * start time in the skeletal data)
 */
export class PoserClock {
  private clock = new Clock();

  private _delta = 0;
  private _gameDelta = 0;

  private _utcOffset = 5; // default to mid-US

  private _internal = 0;
  private _timerRef = 0;
  private _real = 0;
  private _loop = false;
  private _preAndPostPlayBuffer = 3;

  speed = 1;

  // fps calculation
  fps = 0;
  private _frameTimes: number[] = [];
  private _frameCursor = 0;
  private _numFrames = 0;
  private _frameHistoryWindow = 20;
  private _totalFPS = 0;

  private _timelineLimits: [number, number] = [0, 0];
  private _bufferLimits: [number, number] = [0, 0];
  private _playLimits?: [number, number];

  tick(playing = false): number {
    // browser time since last tick
    this._delta = this.clock.getDelta();
    this._internal += this._delta;
    let _currentFrameTime;

    //calculate fps
    if (this._delta !== 0) {
      _currentFrameTime = 1 / this._delta;
      // add the current fps and remove the oldest fps
      this._totalFPS +=
        _currentFrameTime - (this._frameTimes[this._frameCursor] || 0);

      // record the newest fps
      this._frameTimes[this._frameCursor++] = _currentFrameTime;

      // needed so the first N frames, before we have frameHistoryWindow, is correct.
      this._numFrames = Math.max(this._numFrames, this._frameCursor);

      // wrap the cursor
      this._frameCursor %= this._frameHistoryWindow;

      this.fps = parseInt((this._totalFPS / this._numFrames).toFixed(1));
    }

    // in game time
    this._gameDelta = playing ? this.speed * this._delta : 0;

    if (this._gameDelta !== 0 && !this.atBufferLimit()) {
      const oldReal = this._real;
      this._real = clipTime(
        Math.max(
          Math.min(this._real + this._gameDelta, this._bufferLimits[1]),
          this._bufferLimits[0]
        ),
        this.playLimits ? this.playLimits : this.timelineLimits,
        this._loop
      );
      this._gameDelta = this._real - oldReal;
    }

    return this._delta;
  }

  update(buffer: BufferManager) {
    const frames = buffer.frames;

    let bufferStart = +Infinity;
    let bufferEnd = -Infinity;
    const bufferLength = frames.getBufferLength();
    for (let i = 0; i < bufferLength; i++) {
      const frame = frames.get(i);
      if (frame) {
        if (frame.time < bufferStart) bufferStart = frame.time;
        if (frame.time > bufferEnd) bufferEnd = frame.time;
      }
    }
    this.setBufferLimits([bufferStart, bufferEnd]);
    this.timelineLimits = [
      Math.min(this.bufferLimits[0], this.timelineLimits[0]),
      Math.max(this.bufferLimits[1], this.timelineLimits[1]),
    ];

    // handle fact that startTimestamp of record might not be exactly timestamp
    // of first frame
    if (
      this.real < this.bufferLimits[0] &&
      this.bufferLimits[0] - this.real < buffer.marginSeconds
    ) {
      this.real = this.bufferLimits[0];
    }
  }

  resetReference(offset: number) {
    this._timerRef = this._internal - offset;
  }

  atBufferLimit(): boolean {
    return (
      this._real >= this._bufferLimits[1] || this._real < this._bufferLimits[0]
    );
  }

  atPlayLimit(): boolean {
    return (
      this._playLimits !== undefined &&
      (this._real >= this._playLimits[1] || this._real < this._playLimits[0])
    );
  }

  getTimeOfDay(debugOffsetHr: number = 0): TimeOfDay {
    const localHr = (this.realLocal % SECS_IN_DAY) / 60 / 60 + debugOffsetHr;
    const truncatedHours = localHr < 0 ? 24 + localHr : localHr;

    if (truncatedHours >= 17 && truncatedHours < 20) {
      return "SUNSET";
    } else if (truncatedHours >= 20 || truncatedHours < 5) {
      return "NIGHT";
    } else {
      return "DAY";
    }
  }

  set utcOffsetHr(offset: number | undefined) {
    if (offset != undefined && offset != this._utcOffset) {
      this._utcOffset = offset;
    }
  }

  get utcOffsetHr(): number {
    return this._utcOffset;
  }

  set loop(loop: boolean) {
    this._loop = loop;
  }

  get loop(): boolean {
    return this._loop;
  }

  // timeline limits
  set timelineLimits(limits: [number, number | undefined]) {
    if (limits[1] == undefined) {
      limits[1] = limits[0];
    }

    if (limits[0] > limits[1]) {
      console.error(`invalid clock limits ${limits}`);
      limits[1] = limits[0];
    }

    this._timelineLimits = [limits[0], limits[1]];
  }

  get timelineLimits(): [number, number] {
    return this._timelineLimits;
  }

  get timelinePeriod(): number {
    return this._timelineLimits[1] - this._timelineLimits[0];
  }

  // buffer limits
  private setBufferLimits(limits: [number, number | undefined]) {
    if (limits[1] == undefined) {
      limits[1] = limits[0];
    }

    if (limits[0] > limits[1]) {
      console.error(`invalid clock limits ${limits}`);
      limits[1] = limits[0];
    }

    this._bufferLimits = [limits[0], limits[1]];
  }

  get bufferLimits(): [number, number] {
    return this._bufferLimits;
  }

  get bufferPeriod(): number {
    return this._bufferLimits[1] - this._bufferLimits[0];
  }

  // play limits
  set playLimits(limits: [number, number] | undefined) {
    if (!limits) return;
    if (limits[1] == undefined) {
      limits[1] = limits[0];
    }

    if (limits[0] > limits[1]) {
      console.error(`invalid clock limits ${limits}`);
      limits[1] = limits[0];
    }

    this._playLimits = [
      limits[0] - this._preAndPostPlayBuffer,
      limits[1] + this._preAndPostPlayBuffer,
    ];

    if (
      this._real <= this._playLimits[0] &&
      this._real >= this._playLimits[1]
    ) {
      console.error(
        "seekTo beyond play limits. Begin:",
        this._playLimits[0],
        "End:",
        this._playLimits[1]
      );
      return;
    }
  }

  get playLimits(): [number, number] | undefined {
    return this._playLimits;
  }

  get playPeriod(): number | undefined {
    return this._playLimits !== undefined
      ? this._playLimits[1] - this._playLimits[0]
      : undefined;
  }

  // relative time
  get relative(): number {
    return this._real - this._timelineLimits[0];
  }

  get relativePlay(): number | undefined {
    return this._playLimits !== undefined
      ? this._real - this._playLimits[0]
      : undefined;
  }

  // epoch time
  set real(epoch: number) {
    // outside limits, represents a jump, so reset limits
    if (epoch < this._timelineLimits[0] || epoch > this._timelineLimits[1]) {
      this.timelineLimits = [epoch, epoch];
    }

    if (epoch < this._bufferLimits[0] || epoch > this._bufferLimits[1]) {
      this.setBufferLimits([epoch, epoch]);
    }

    this._real = epoch;
  }

  get real(): number {
    return this._real;
  }

  // TODO: deprecate and do everything in seconds
  get realMs(): number {
    return this.real * 1000;
  }

  get realLocal(): number {
    return this.real + SECS_IN_HOUR * this.utcOffsetHr;
  }

  get realLocalMs(): number {
    return this.realLocal * 1000;
  }

  // deltas and fps
  get delta(): number {
    return this._delta;
  }

  get gameDelta(): number {
    return this._gameDelta;
  }

  get avgFps(): number {
    return this.fps;
  }

  get elapsedTime(): number {
    return this._internal - this._timerRef;
  }

  equals(other: PoserClock): boolean {
    return (
      this._real == other._real &&
      arraysEqual(this._bufferLimits, other._bufferLimits) &&
      arraysEqual(this._timelineLimits, other._timelineLimits)
    );
  }

  static clone(clock: PoserClock): PoserClock {
    const clone = new PoserClock();
    Object.assign(clone, clock);
    return clone;
  }
}

/**
 * Wraps time increment into period
 * @param t
 * @param increment
 * @returns
 */
function clipTime(
  t: number,
  limits: [number, number],
  loop: boolean = false
): number {
  if (t < limits[1]) {
    return t;
  } else if (loop) {
    t = limits[0];
  } else {
    t = limits[1];
  }

  return t;
}

export function calculateAlpha(
  range: [number, number],
  time: number
): number | undefined {
  const t0 = range[0];
  const t1 = range[1];
  const period = t1 - t0;
  const alpha = period > 0 ? (time - t0) / period : 0;

  if (alpha < 0 || alpha > 1) {
    const tMin = Math.min(t0, t1, time);
    // TODO: fail silently here?
    console.error(
      `interpolation alpha (${alpha}) outside of bounds tMin: ${tMin} t: ${
        time - tMin
      } t0: ${t0 - tMin} t1: ${t1 - tMin}`
    );
    return undefined;
  }

  return alpha;
}
